import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCClientAPI, useTRPCMutation, useTRPCQuery } from '../../../../trpc/trpc';
import type { EditorialPitchStatus } from '../outreachTypes';

/**
 * The "I pitched it" mark of the Spotify editorial pitch of one song (EMAILS.md E6). The mark stops
 * the reminders of day -14 and day -8. Every member may mark and undo.
 */
export function useS4aStatus(accountId: number, catalogItemUuid: string) {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();
  const query = useTRPCQuery(
    trpcAPI.outreach.s4a.status.queryOptions({ accountId, catalogItemUuid }, { retry: false })
  );
  // The answer of the last write shows at once; the next read of the query replaces it.
  const [written, setWritten] = useState<EditorialPitchStatus | null>(null);
  useEffect(() => setWritten(null), [query.dataUpdatedAt, catalogItemUuid]);

  const onWritten = useCallback(
    (answer: EditorialPitchStatus) => {
      setWritten(answer);
      queryClient.invalidateQueries({ queryKey: trpcAPI.outreach.s4a.status.pathKey() }).catch(console.error);
    },
    [queryClient, trpcAPI]
  );

  const mark = useTRPCMutation({
    mutationFn: () =>
      trpcClient.outreach.s4a.markPitched.mutate({ accountId, catalogItemUuid }) as Promise<EditorialPitchStatus>,
    onSuccess: onWritten,
  });
  const unmark = useTRPCMutation({
    mutationFn: () =>
      trpcClient.outreach.s4a.unmarkPitched.mutate({ accountId, catalogItemUuid }) as Promise<EditorialPitchStatus>,
    onSuccess: onWritten,
  });

  return {
    status: written ?? (query.data as EditorialPitchStatus | null | undefined) ?? null,
    isLoading: query.isLoading,
    markPitched: () => mark.mutate(),
    unmarkPitched: () => unmark.mutate(),
    isWriting: mark.isPending || unmark.isPending,
    error: mark.error ?? unmark.error,
  };
}
